import React, { useState } from 'react';
import './Past.css';

// Import des icônes
import iconPast from '../../../../assets/icons/Icon-past.png';
import iconDNA from '../../../../assets/icons/Icon-DNA.png';
import iconModify from '../../../../assets/icons/Icon-modify.png';
import iconIg from '../../../../assets/icons/ig.png';
import iconHeart from '../../../../assets/icons/coeur.png';

// Import des données fake
import {
  defaultFamilyMedicalData,
  relationTypeOptions,
} from '../../../fakedata/familyMedicalData';

const Past = ({
  familyHistoryData = defaultFamilyMedicalData,
  defaultTab = 'past',
  onSeeAll = () => {},
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Data states
  const [pastIllnesses, setPastIllnesses] = useState(
    Array.isArray(pastIllnessesData) ? pastIllnessesData : []
  );
  const [familyHistory, setFamilyHistory] = useState(
    Array.isArray(familyHistoryData) ? familyHistoryData : []
  );

  // Modal states
  const [openPastModal, setOpenPastModal] = useState(false);
  const [openFamilyModal, setOpenFamilyModal] = useState(false);

  // Past Illness modal states
  const [pastEdit, setPastEdit] = useState([]);
  const [pastForm, setPastForm] = useState({
    illness: '',
    startDate: '',
    endDate: '',
  });
  const [pastErrors, setPastErrors] = useState({});

  // Family History modal states
  const [familyEdit, setFamilyEdit] = useState([]);
  const [familyForm, setFamilyForm] = useState({
    condition: '',
    familyMember: '',
    relationType: '',
  });
  const [familyErrors, setFamilyErrors] = useState({});

  const isPast = activeTab === 'past';
  const isFamily = activeTab === 'family';

  // Family member options
  const familyMemberOptions = [
    'Father',
    'Mother',
    'Grandfather',
    'Grandmother',
    'Brother',
    'Sister',
    'Uncle',
    'Aunt',
  ];

  // Open modify modal
  const openModify = () => {
    if (isPast) {
      setPastEdit(pastIllnesses);
      setPastForm({ illness: '', startDate: '', endDate: '' });
      setPastErrors({});
      setOpenPastModal(true);
    } else {
      setFamilyEdit(familyHistory);
      setFamilyForm({ condition: '', familyMember: '', relationType: '' });
      setFamilyErrors({});
      setOpenFamilyModal(true);
    }
  };

  // ======== Past Illness helpers ========
  const validatePast = (data) => {
    const e = {};
    if (!data.illness?.trim()) e.illness = 'Required';
    if (!data.startDate?.trim()) e.startDate = 'Required';
    if (!data.endDate?.trim()) e.endDate = 'Required';
    return e;
  };

  const removePast = (id) =>
    setPastEdit((prev) => prev.filter((x) => x.id !== id));

  // Add new past illness row on Enter key
  const addPastOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const hasAny =
        pastForm.illness.trim() !== '' ||
        pastForm.startDate.trim() !== '' ||
        pastForm.endDate.trim() !== '';

      if (hasAny) {
        const errs = validatePast(pastForm);
        setPastErrors(errs);
        if (Object.keys(errs).length) return;

        setPastEdit((prev) => [
          ...prev,
          {
            id: `pi-${Date.now()}`,
            ...pastForm,
          },
        ]);
        setPastForm({ illness: '', startDate: '', endDate: '' });
        setPastErrors({});
      }
    }
  };

  const savePast = () => {
    setPastIllnesses(pastEdit);
    setOpenPastModal(false);
    setPastForm({ illness: '', startDate: '', endDate: '' });
    setPastErrors({});
  };

  const discardPast = () => {
    setOpenPastModal(false);
    setPastForm({ illness: '', startDate: '', endDate: '' });
    setPastErrors({});
  };

  // ======== Family History helpers ========
  const validateFamily = (data) => {
    const e = {};
    if (!data.condition?.trim()) e.condition = 'Required';
    if (!data.familyMember?.trim()) e.familyMember = 'Required';
    if (!data.relationType?.trim()) e.relationType = 'Required';
    return e;
  };

  const removeFamily = (id) =>
    setFamilyEdit((prev) => prev.filter((x) => x.id !== id));

  // Add new family history row on Enter key
  const addFamilyOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const hasAny =
        familyForm.condition.trim() !== '' ||
        familyForm.familyMember.trim() !== '' ||
        familyForm.relationType.trim() !== '';

      if (hasAny) {
        const errs = validateFamily(familyForm);
        setFamilyErrors(errs);
        if (Object.keys(errs).length) return;

        setFamilyEdit((prev) => [
          ...prev,
          { id: `fh-${Date.now()}`, ...familyForm },
        ]);
        setFamilyForm({
          condition: '',
          familyMember: '',
          relationType: '',
        });
        setFamilyErrors({});
      }
    }
  };

  const saveFamily = () => {
    setFamilyHistory(familyEdit);
    setOpenFamilyModal(false);
    setFamilyForm({
      condition: '',
      familyMember: '',
      relationType: '',
    });
    setFamilyErrors({});
  };

  const discardFamily = () => {
    setOpenFamilyModal(false);
    setFamilyForm({
      condition: '',
      familyMember: '',
      relationType: '',
    });
    setFamilyErrors({});
  };

  return (
    <div className="past-card">
      {/* Header + tabs */}
      <div className="past-card-header">
        <div
          className="past-card-header-left"
          role="tablist"
          aria-label="Medical history sections"
        >
          <button
            className={`past-card-tab-btn ${
              isPast ? 'past-card-tab-btn--active' : ''
            }`}
            onClick={() => setActiveTab('past')}
            type="button"
          >
            <img
              src={iconPast}
              alt=""
              className="past-card-past-icon"
            />
            <span className="past-card-tab-label">Past Illnesses</span>
          </button>

          <button
            className={`past-card-tab-btn ${
              isFamily ? 'past-card-tab-btn--active' : ''
            }`}
            onClick={() => setActiveTab('family')}
            type="button"
          >
            <img
              src={iconDNA}
              alt=""
              className="past-card-dna-icon"
            />
            <span className="past-card-tab-label">
              Family Medical History
            </span>
          </button>
        </div>

        <button
          className="past-card-modify-btn"
          type="button"
          title="Modify"
          onClick={openModify}
        >
          <img
            src={iconModify}
            alt="Modify"
            className="past-card-modify-icon"
          />
        </button>
      </div>

      {/* Content */}
      <div className="past-card-content">
        {isPast && (
          <div id="tab-panel-past" className="past-card-panel">
            <p className="past-card-description">
              List of past illnesses in neat rows
            </p>
            {pastIllnesses.map((illness) => (
              <div
                key={illness.id}
                className="past-card-illness-card"
              >
                <div className="past-card-illness-card-left">
                  <div className="past-card-illness-icon-wrapper">
                    <img
                      src={iconIg}
                      alt=""
                      className="past-card-illness-icon"
                    />
                  </div>
                  <div className="past-card-illness-info">
                    <span className="past-card-illness-name">
                      {illness.illness}
                    </span>
                  </div>
                </div>
                <div className="past-card-illness-dates">
                  <span className="past-card-illness-date-text">
                    {illness.startDate}
                  </span>
                  {illness.endDate &&
                    illness.endDate !== 'Under control' && (
                      <span className="past-card-illness-date-text">
                        {' '}
                        - {illness.endDate}
                      </span>
                    )}
                  {illness.endDate === 'Under control' && (
                    <span className="past-card-illness-status">
                      Under control
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {isFamily && (
          <div
            id="tab-panel-family"
            className="past-card-panel"
          >
            <p className="past-card-description">
              List of family medical history in neat rows
            </p>
            {familyHistory.map((history) => (
              <div
                key={history.id}
                className="past-card-family-card"
              >
                <div className="past-card-family-card-left">
                  <div className="past-card-family-icon-wrapper">
                    <img
                      src={iconHeart}
                      alt=""
                      className="past-card-family-icon"
                    />
                  </div>
                  <div className="past-card-family-info">
                    <span className="past-card-family-member-name">
                      {history.familyMember} ({history.relationType})
                    </span>
                    <span className="past-card-family-condition">
                      : {history.condition}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========= MODAL Past Illness ========= */}
      {openPastModal && (
        <div
          className="past-modal-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="past-modal-card">
            <div className="past-modal-header">
              <div className="past-modal-title">
                Past Illness Card
              </div>
              <button
                className="past-modal-close"
                onClick={discardPast}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M10.5 3.5L3.5 10.5"
                    stroke="#546881"
                    strokeWidth="0.8"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.5 3.5L10.5 10.5"
                    stroke="#546881"
                    strokeWidth="0.8"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="past-modal-body">
              <div className="past-modal-list">
                {pastEdit.map((p) => (
                  <div
                    className="past-modal-row"
                    key={p.id}
                  >
                    <div className="past-modal-pill past-modal-pill--name">
                      {p.illness}
                    </div>
                    <div className="past-modal-pill past-modal-pill--sm">
                      {p.startDate}
                    </div>
                    <div className="past-modal-pill past-modal-pill--sm">
                      {p.endDate}
                    </div>
                    <button
                      className="past-modal-trash"
                      onClick={() => removePast(p.id)}
                      title="Delete"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M12.6666 4V13.3333C12.6666 13.687 12.5261 14.0261 12.2761 14.2761C12.026 14.5262 11.6869 14.6667 11.3333 14.6667H4.66659C4.31296 14.6667 3.97382 14.5262 3.72378 14.2761C3.47373 14.0261 3.33325 13.687 3.33325 13.3333V4"
                          stroke="#E46B6B"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 4H14"
                          stroke="#E46B6B"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.33325 4.00016V2.66683C5.33325 2.31321 5.47373 1.97407 5.72378 1.72402C5.97383 1.47397 6.31296 1.3335 6.66659 1.3335H9.33325C9.68687 1.3335 10.026 1.47397 10.2761 1.72402C10.5261 1.97407 10.6666 2.31321 10.6666 2.66683V4.00016"
                          stroke="#E46B6B"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <div className="past-modal-form-wrap">
                <div className="past-modal-section-title">
                  Register new illness
                </div>

                <div className="past-modal-form past-modal-form--past">
                  <div className="past-modal-field">
                    <label className="past-modal-label">
                      Illness name{' '}
                      <span className="past-modal-req">*</span>
                    </label>
                    <input
                      className={`past-modal-input past-modal-input--name ${
                        pastErrors.illness
                          ? 'past-modal-input--error'
                          : ''
                      }`}
                      type="text"
                      placeholder="Add illness name"
                      value={pastForm.illness}
                      onChange={(e) =>
                        setPastForm((s) => ({
                          ...s,
                          illness: e.target.value,
                        }))
                      }
                      onKeyDown={addPastOnEnter}
                    />
                    {pastErrors.illness && (
                      <div className="past-modal-err">
                        Required
                      </div>
                    )}
                  </div>

                  <div className="past-modal-field">
                    <label className="past-modal-label">
                      Start Date{' '}
                      <span className="past-modal-req">*</span>
                    </label>
                    <div className="past-modal-date-wrap">
                      <input
                        type="date"
                        className={`past-modal-input past-modal-input--sm ${
                          pastErrors.startDate
                            ? 'past-modal-input--error'
                            : ''
                        }`}
                        value={pastForm.startDate}
                        onChange={(e) =>
                          setPastForm((s) => ({
                            ...s,
                            startDate: e.target.value,
                          }))
                        }
                        onKeyDown={addPastOnEnter}
                      />
                      <button
                        type="button"
                        className="past-modal-cal"
                        tabIndex={-1}
                        aria-hidden
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M2.666 6.666h10.667M11.333 2v2M4.666 2v2M4 14h8a1.333 1.333 0 0 0 1.333-1.333V4.667A1.333 1.333 0 0 0 12 3.333H4A1.333 1.333 0 0 0 2.667 4.667v8A1.333 1.333 0 0 0 4 14Z"
                            stroke="#546881"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    {pastErrors.startDate && (
                      <div className="past-modal-err">
                        Required
                      </div>
                    )}
                  </div>

                  <div className="past-modal-field">
                    <label className="past-modal-label">
                      End Date{' '}
                      <span className="past-modal-req">*</span>
                    </label>
                    <div className="past-modal-date-wrap">
                      <input
                        type="date"
                        className={`past-modal-input past-modal-input--sm ${
                          pastErrors.endDate
                            ? 'past-modal-input--error'
                            : ''
                        }`}
                        value={pastForm.endDate}
                        onChange={(e) =>
                          setPastForm((s) => ({
                            ...s,
                            endDate: e.target.value,
                          }))
                        }
                        onKeyDown={addPastOnEnter}
                      />
                      <button
                        type="button"
                        className="past-modal-cal"
                        tabIndex={-1}
                        aria-hidden
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M2.666 6.666h10.667M11.333 2v2M4.666 2v2M4 14h8a1.333 1.333 0 0 0 1.333-1.333V4.667A1.333 1.333 0 0 0 12 3.333H4A1.333 1.333 0 0 0 2.667 4.667v8A1.333 1.333 0 0 0 4 14Z"
                            stroke="#546881"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    {pastErrors.endDate && (
                      <div className="past-modal-err">
                        Required
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="past-modal-footer">
              <button
                className="past-modal-btn past-modal-btn--ghost"
                onClick={discardPast}
              >
                Discard changes
              </button>
              <button
                className="past-modal-btn past-modal-btn--primary"
                onClick={savePast}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========= MODAL Family History ========= */}
      {openFamilyModal && (
        <div
          className="past-modal-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div className="past-modal-card">
            <div className="past-modal-header">
              <div className="past-modal-title">
                Family Medical History
              </div>
              <button
                className="past-modal-close"
                onClick={discardFamily}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M10.5 3.5L3.5 10.5"
                    stroke="#546881"
                    strokeWidth="0.8"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.5 3.5L10.5 10.5"
                    stroke="#546881"
                    strokeWidth="0.8"
                    strokeLinecap="square"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div className="past-modal-body">
              <div className="past-modal-list">
                {familyEdit.map((f) => (
                  <div
                    className="past-modal-row"
                    key={f.id}
                  >
                    <div className="past-modal-pill past-modal-pill--name">
                      {f.condition}
                    </div>
                    <div className="past-modal-pill past-modal-pill--sm">
                      {f.familyMember}
                    </div>
                    <div className="past-modal-pill past-modal-pill--sm">
                      {f.relationType}
                    </div>
                    <button
                      className="past-modal-trash"
                      onClick={() => removeFamily(f.id)}
                      title="Delete"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M12.6666 4V13.3333C12.6666 13.687 12.5261 14.0261 12.2761 14.2761C12.026 14.5262 11.6869 14.6667 11.3333 14.6667H4.66659C4.31296 14.6667 3.97382 14.5262 3.72378 14.2761C3.47373 14.0261 3.33325 13.687 3.33325 13.3333V4"
                          stroke="#E46B6B"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 4H14"
                          stroke="#E46B6B"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.33325 4.00016V2.66683C5.33325 2.31321 5.47373 1.97407 5.72378 1.72402C5.97383 1.47397 6.31296 1.3335 6.66659 1.3335H9.33325C9.68687 1.3335 10.026 1.47397 10.2761 1.72402C10.5261 1.97407 10.6666 2.31321 10.6666 2.66683V4.00016"
                          stroke="#E46B6B"
                          strokeWidth="0.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <div className="past-modal-form-wrap">
                <div className="past-modal-section-title">
                  Register new family medical history
                </div>

                <div className="past-modal-form past-modal-form--family">
                  <div className="past-modal-field">
                    <label className="past-modal-label">
                      Condition{' '}
                      <span className="past-modal-req">*</span>
                    </label>
                    <input
                      className={`past-modal-input past-modal-input--name ${
                        familyErrors.condition
                          ? 'past-modal-input--error'
                          : ''
                      }`}
                      type="text"
                      placeholder="Add Condition"
                      value={familyForm.condition}
                      onChange={(e) =>
                        setFamilyForm((s) => ({
                          ...s,
                          condition: e.target.value,
                        }))
                      }
                      onKeyDown={addFamilyOnEnter}
                    />
                    {familyErrors.condition && (
                      <div className="past-modal-err">
                        Required
                      </div>
                    )}
                  </div>

                  <div className="past-modal-field">
                    <label className="past-modal-label">
                      Family member{' '}
                      <span className="past-modal-req">*</span>
                    </label>
                    <select
                      className={`past-modal-input past-modal-input--sm ${
                        familyErrors.familyMember
                          ? 'past-modal-input--error'
                          : ''
                      }`}
                      value={familyForm.familyMember}
                      onChange={(e) =>
                        setFamilyForm((s) => ({
                          ...s,
                          familyMember: e.target.value,
                        }))
                      }
                      onKeyDown={addFamilyOnEnter}
                    >
                      <option value="">Family member</option>
                      {familyMemberOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {familyErrors.familyMember && (
                      <div className="past-modal-err">
                        Required
                      </div>
                    )}
                  </div>

                  <div className="past-modal-field">
                    <label className="past-modal-label">
                      Relation type{' '}
                      <span className="past-modal-req">*</span>
                    </label>
                    <select
                      className={`past-modal-input past-modal-input--sm ${
                        familyErrors.relationType
                          ? 'past-modal-input--error'
                          : ''
                      }`}
                      value={familyForm.relationType}
                      onChange={(e) =>
                        setFamilyForm((s) => ({
                          ...s,
                          relationType: e.target.value,
                        }))
                      }
                      onKeyDown={addFamilyOnEnter}
                    >
                      <option value="">Relationship</option>
                      {relationTypeOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {familyErrors.relationType && (
                      <div className="past-modal-err">
                        Required
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="past-modal-footer">
              <button
                className="past-modal-btn past-modal-btn--ghost"
                onClick={discardFamily}
              >
                Discard changes
              </button>
              <button
                className="past-modal-btn past-modal-btn--primary"
                onClick={saveFamily}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Past;
